import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { listingsAPI } from "../api/index.js";
import { useAuth } from "../hooks/useAuth.jsx";
import SEO from "../components/SEO.jsx";

const G = {
  bg:"#0A0A0A", surface:"#111111", border:"#1E1E1E",
  text:"#E4E4DC", muted:"#666", accent:"#00C896",
  danger:"#FF6B6B", font:"'Sora', sans-serif", mono:"'DM Mono', monospace",
};

const CATEGORIES = ["Forex Broker","Crypto Exchange","Stock Broker","Investment Platform","Other"];

const Input = ({ label, required, ...props }) => (
  <div style={{ marginBottom:16 }}>
    <label style={{ display:"block", fontSize:11, color:G.muted, fontFamily:G.mono, letterSpacing:0.5, textTransform:"uppercase", marginBottom:6 }}>
      {label}{required && <span style={{ color:G.danger }}> *</span>}
    </label>
    <input style={{ width:"100%", background:"#141414", border:`1px solid #2A2A2A`, color:G.text, borderRadius:8, padding:"11px 14px", fontSize:14, fontFamily:G.font, outline:"none", boxSizing:"border-box" }}
      onFocus={e => e.target.style.borderColor=G.accent}
      onBlur={e => e.target.style.borderColor="#2A2A2A"}
      {...props} />
  </div>
);

export default function SubmitListingPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({
    name:"", description:"", website:"", category:"",
    country:"", founded_year:"", regulator:""
  });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  if (!user) { navigate("/login"); return null; }

  const handleSubmit = async () => {
    if (!form.name.trim()) return setError("Name is required");
    if (!form.category) return setError("Category is required");
    setSubmitting(true); setError("");
    try {
      const res = await listingsAPI.create({
        ...form,
        founded_year: form.founded_year ? Number(form.founded_year) : undefined,
      });
      navigate(`/listings/${res.data.listing.slug}`);
    } catch (e) {
      setError(e.response?.data?.errors?.[0]?.msg || e.response?.data?.error || "Failed to submit");
    } finally { setSubmitting(false); }
  };

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  return (
    <div style={{ maxWidth:640, margin:"0 auto", padding:"36px 16px", fontFamily:G.font }}>
      <SEO title="Submit a Listing" noindex />
      <Link to="/listings" style={{ textDecoration:"none", color:G.muted, fontSize:13, fontFamily:G.mono }}>← Back to Listings</Link>
      <h1 style={{ fontSize:26, fontWeight:700, color:G.text, margin:"18px 0 6px" }}>Submit a Listing</h1>
      <p style={{ color:G.muted, fontSize:14, marginBottom:28 }}>Add a broker, exchange or platform to the FOGRA directory.</p>

      <Input label="Company Name" required placeholder="e.g. Acme Forex Broker" value={form.name} onChange={set("name")} />

      <div style={{ marginBottom:16 }}>
        <label style={{ display:"block", fontSize:11, color:G.muted, fontFamily:G.mono, letterSpacing:0.5, textTransform:"uppercase", marginBottom:6 }}>Category <span style={{ color:G.danger }}>*</span></label>
        <select value={form.category} onChange={set("category")}
          style={{ width:"100%", background:"#141414", border:`1px solid #2A2A2A`, color:form.category?G.text:G.muted, borderRadius:8, padding:"11px 14px", fontSize:14, fontFamily:G.font, outline:"none", cursor:"pointer" }}>
          <option value="">Select category...</option>
          {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
        </select>
      </div>

      <div style={{ marginBottom:16 }}>
        <label style={{ display:"block", fontSize:11, color:G.muted, fontFamily:G.mono, letterSpacing:0.5, textTransform:"uppercase", marginBottom:6 }}>Description</label>
        <textarea value={form.description} onChange={set("description")} rows={4}
          placeholder="Brief description of the company, its services and what it offers..."
          style={{ width:"100%", background:"#141414", border:`1px solid #2A2A2A`, color:G.text, borderRadius:8, padding:"11px 14px", fontSize:14, fontFamily:G.font, resize:"vertical", outline:"none", lineHeight:1.6, boxSizing:"border-box" }}
          onFocus={e => e.target.style.borderColor=G.accent}
          onBlur={e => e.target.style.borderColor="#2A2A2A"} />
      </div>

      <Input label="Website" placeholder="https://example.com" value={form.website} onChange={set("website")} />
      <Input label="Country" placeholder="e.g. United Kingdom" value={form.country} onChange={set("country")} />
      <Input label="Founded Year" type="number" placeholder="e.g. 2010" value={form.founded_year} onChange={set("founded_year")} />
      <Input label="Regulator(s)" placeholder="e.g. FCA, CySEC, ASIC" value={form.regulator} onChange={set("regulator")} />

      <div style={{ background:"#141414", border:`1px solid #2A2A2A`, borderRadius:8, padding:14, marginBottom:20, fontSize:13, color:G.muted, lineHeight:1.6 }}>
        ℹ️ Submissions are reviewed by our team before being verified. All listings are publicly visible immediately but verification badge requires admin approval.
      </div>

      {error && <div style={{ color:G.danger, fontSize:13, marginBottom:16, fontFamily:G.mono }}>{error}</div>}

      <button onClick={handleSubmit} disabled={submitting||!form.name.trim()||!form.category}
        style={{ background:G.accent, border:"none", color:"#000", padding:"12px 32px", borderRadius:24, cursor:"pointer", fontFamily:G.mono, fontWeight:700, fontSize:14, opacity:submitting||!form.name.trim()||!form.category?0.4:1 }}>
        {submitting?"Submitting...":"Submit Listing →"}
      </button>
    </div>
  );
}
